import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, FileText, Link2 } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import dreamWorldProvider from "../DreamWorldProvider";
import PageLayout from "../components/ui/PageLayout";

const SettingsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentUser = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const [profileData, setProfileData] = useState({
    bio: "",
    socials: {
      twitter: "",
      facebook: "",
      instagram: "",
      tiktok: "",
      snap: "",
    },
    avatar: "",
    imageBack: "",
    username: "",
    email: "",
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [imageBackFile, setImageBackFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        const userData = await dreamWorldProvider.getuserData(
          currentUser?.user.uid
        );
        if (userData) {
          setProfileData({
            bio: userData.bio || "",
            socials: {
              twitter: userData.socials?.twitter || "",
              facebook: userData.socials?.facebook || "",
              instagram: userData.socials?.instagram || "",
              tiktok: userData.socials?.tiktok || "",
              snap: userData.socials?.snap || "",
            },
            avatar: userData.avatarUrl || "",
            imageBack: userData.backImageUrl || "",
            username: userData.username || "",
            email: userData.email || "",
          });
        }
      }
    };
    fetchUserData();
  }, [currentUser]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name in profileData.socials) {
      setProfileData((prev) => ({
        ...prev,
        socials: { ...prev.socials, [name]: value },
      }));
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value as string }));
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "avatar" | "imageBack"
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (fileType === "avatar") {
        setAvatarFile(file);
        setProfileData((prev) => ({
          ...prev,
          avatar: URL.createObjectURL(file),
        }));
      } else {
        setImageBackFile(file);
        setProfileData((prev) => ({
          ...prev,
          imageBack: URL.createObjectURL(file),
        }));
      }
    }
  };

  const handleSave = async () => {
    if (currentUser) {
      const storage = getStorage();
      let avatarUrl = profileData.avatar;
      let imageUrl = profileData.imageBack;

      if (avatarFile) {
        const avatarRef = ref(
          storage,
          `avatars/${currentUser.user.uid}/${avatarFile.name}`
        );
        await uploadBytes(avatarRef, avatarFile);
        avatarUrl = await getDownloadURL(avatarRef);
      }

      if (imageBackFile) {
        const imageBackRef = ref(
          storage,
          `imageBacks/${currentUser.user.uid}/${imageBackFile.name}`
        );
        await uploadBytes(imageBackRef, imageBackFile);
        imageUrl = await getDownloadURL(imageBackRef);
      }

      const updatedData = {
        bio: profileData.bio,
        socials: profileData.socials,
        avatarUrl: avatarUrl,
        backImageUrl: imageUrl,
        username: profileData.username,
        email: profileData.email,
      };

      await dreamWorldProvider.updateUserData(
        currentUser.user.uid,
        updatedData
      );
      alert("Profile updated successfully!");
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
      <PageLayout>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-br from-purple-400 to-pink-400 bg-clip-text text-transparent py-3 px-1">
                Settings
              </h1>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                Manage your account and profile settings
              </p>
            </div>
          </div>
        </motion.div>
        <div className="space-y-8">
          <SettingsCard title="User Information" icon={User}>
            <div className="p-5 space-y-4">
              <div className="flex flex-col">
                <label className="text-sm text-gray-400 mb-1">Username</label>
                <input
                  type="text"
                  name="username"
                  value={profileData.username}
                  onChange={handleInputChange}
                  className="bg-gray-800 border border-gray-700 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  className="bg-gray-800 border border-gray-700 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </SettingsCard>
          <SettingsCard title="Profile Picture" icon={User}>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-4">
                <img
                  src={profileData.avatar || "https://via.placeholder.com/80"}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div>
                  <input
                    type="file"
                    id="avatar-upload"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, "avatar")}
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md text-sm cursor-pointer"
                  >
                    Upload New Picture
                  </label>
                  <p className="text-xs text-gray-400 mt-2">
                    PNG, JPG, GIF up to 10MB.
                  </p>
                </div>
              </div>
            </div>
          </SettingsCard>
          <SettingsCard title="Background Image" icon={User}>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-4">
                <img
                  src={
                    profileData.imageBack ||
                    "https://via.placeholder.com/150x80"
                  }
                  alt="Background"
                  className="w-40 h-20 rounded-md object-cover"
                />
                <div>
                  <input
                    type="file"
                    id="image-back-upload"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, "imageBack")}
                  />
                  <label
                    htmlFor="image-back-upload"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md text-sm cursor-pointer"
                  >
                    Upload New Image
                  </label>
                  <p className="text-xs text-gray-400 mt-2">
                    PNG, JPG, GIF up to 10MB.
                  </p>
                </div>
              </div>
            </div>
          </SettingsCard>
          <SettingsCard title="Profile Information" icon={FileText}>
            <div className="p-5 space-y-4">
              <div className="flex flex-col">
                <label className="text-sm text-gray-400 mb-1">Bio</label>
                <textarea
                  name="bio"
                  value={profileData.bio}
                  onChange={handleInputChange}
                  className="bg-gray-800 border border-gray-700 rounded-md p-2 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                ></textarea>
              </div>
            </div>
          </SettingsCard>
          <SettingsCard title="Social Links" icon={Link2}>
            <div className="p-5 space-y-4">
              {Object.keys(profileData.socials).map((key) => (
                <div className="flex flex-col" key={key}>
                  <label className="text-sm text-gray-400 mb-1 capitalize">
                    {key}
                  </label>
                  <input
                    type="text"
                    name={key}
                    value={
                      profileData.socials[
                        key as keyof typeof profileData.socials
                      ]
                    }
                    onChange={handleInputChange}
                    className="bg-gray-800 border border-gray-700 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              ))}
            </div>
          </SettingsCard>
          <SettingsCard title="Legal Information" icon={FileText}>
            <div className="p-5 space-y-4">
              <a
                href="/about"
                className="text-indigo-400 hover:text-indigo-300"
              >
                About Us
              </a>
              <a
                href="/privacy"
                className="text-indigo-400 hover:text-indigo-300"
              >
                Privacy Policy
              </a>
              <a href="/tos" className="text-indigo-400 hover:text-indigo-300">
                Terms of Service
              </a>
            </div>
          </SettingsCard>
          <div className="p-5 border-t border-gray-800">
            <button
              onClick={handleSave}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-md"
            >
              Save Changes
            </button>
          </div>
        </div>
      </PageLayout>
    </div>
  );
};

const SettingsCard: React.FC<{
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
    <div className="p-5 border-b border-gray-800">
      <h3 className="text-lg font-semibold text-white flex items-center gap-3">
        <Icon size={20} />
        {title}
      </h3>
    </div>
    {children}
  </div>
);

export default SettingsPage;
